<template>
  <nav
    v-if="crumbs.length > 0"
    class="breadcrumb"
  >
    <template
      v-for="(crumb, index) in crumbs"
      :key="index"
    >
      <template v-if="index < crumbs.length - 1">
        <component
          :is="crumb.path ? 'RouterLink' : 'span'"
          :to="crumb.path"
          class="breadcrumb-before-link"
          :class="{ 'breadcrumb-link': !!crumb.path }"
        >
          {{ crumb.name }}
        </component>
        <span class="breadcrumb-separator">/</span>
      </template>

      <span
        v-else
        class="breadcrumb-current-link"
        :title="crumb.name"
      >
        <VIcon
          v-if="currentIcon"
          v-bind="currentIcon"
          size="20"
          class="me-2"
        />
        <span class="breadcrumb-current-label">{{ crumb.name }}</span>
      </span>
    </template>
  </nav>
</template>

<script setup>
import { computed } from 'vue';

import { useRoute } from 'vue-router';

import { breadcrumbTitleOverride } from '@/composables/useBreadcrumbTitle';
import navigationItems from '@/navigation/vertical';

const props = defineProps({
  items: {
    type: Array,
    default: () => [],
  },
});

defineOptions({
  name: 'UiBreadcrumbs',
});

const route = useRoute();

// Use Set for O(1) lookups instead of Array.includes()
const excludedRoutes = new Set([
  '/', '/login', '/register', '/reset-password', '/forgot-password',
  '/404', '/account', '/welcome', '/settings/profile', '/connect-accounts',
]);

// Single RegExp to match multiple exclusion patterns (faster than multiple startsWith calls)
const excludedRegex = /^\/(auth|maintenance)\//;

/**
 * Find navigation path (array of items) for the current route recursively
 */
const findNavigationPath = (items, targetRoute, path = []) => {
  for (const item of items) {
    let isMatch = false;

    if (item.to) {
      if (typeof item.to === 'string') {
        isMatch = item.to === targetRoute.name || item.to === targetRoute.path;
      } else if (typeof item.to === 'object') {
        isMatch = item.to.name === targetRoute.name || item.to.path === targetRoute.path;
      }
    }

    if (isMatch) {
      return [...path, item];
    }

    if (item.children) {
      const result = findNavigationPath(item.children, targetRoute, [...path, item]);
      if (result) return result;
    }
  }
  return null;
};

const currentNavigationPath = computed(() => {
  return findNavigationPath(navigationItems, route) || [];
});

const currentIcon = computed(() => {
  const lastItem = currentNavigationPath.value.at(-1);
  return lastItem?.icon || null;
});

// Path segments whose plain title-casing is unclear get an explicit label
// (e.g. "sp" → "Selling Partner", Amazon's own term for the SP-API integration).
const SEGMENT_LABELS = {
  sp: 'Selling Partner',
};

/**
 * Formats a route segment into a readable name
 */
const formatSegmentName = (segment) => {
  if (!segment) return '';

  const mapped = SEGMENT_LABELS[segment.toLowerCase()];
  if (mapped) return mapped;

  return segment
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Resolves the route destination from a navigation item
 */
const resolveRoutePath = (to) => {
  if (!to) return null;
  if (typeof to === 'string') return to.startsWith('/') ? to : { name: to };
  if (typeof to === 'object' && (to.name || to.path)) return to;
  return null;
};

// Create breadcrumbs based on the current route
const crumbs = computed(() => {
  // Check if current route is in the exclusion list
  if (excludedRoutes.has(route.path) || excludedRegex.test(route.path)) {
    return [];
  }

  // If specific items are provided, use them
  if (props.items.length > 0) {
    return props.items;
  }

  // Build breadcrumbs based on navigation path if available
  if (currentNavigationPath.value.length > 0) {
    return currentNavigationPath.value.map((item, index) => {
      const isLast = index === currentNavigationPath.value.length - 1;

      return {
        name: item.title,
        path: isLast ? null : resolveRoutePath(item.to),
      };
    });
  }

  const currentPath = route.path;
  const pathSegments = currentPath.split('/').filter(Boolean);

  // A page that is not a nav item but declares `navActiveLink` sits under that item:
  // its trail is the item's nav path plus the page itself.
  const activeLink = route.meta?.navActiveLink;
  const parentPath = activeLink
    ? findNavigationPath(navigationItems, { name: activeLink, path: '' })
    : null;

  if (parentPath) {
    return [
      ...parentPath.map((item) => ({ name: item.title, path: resolveRoutePath(item.to) })),
      {
        name: breadcrumbTitleOverride.value || formatSegmentName(pathSegments.at(-1)),
        path: null,
      },
    ];
  }

  // Fallback: build breadcrumbs based on the current route segments

  let currentPathBuilder = '';

  return pathSegments.map((segment, index) => {
    currentPathBuilder += `/${segment}`;
    const segmentLower = segment.toLowerCase();

    // We search the entire navigation tree for a matching title or route name for this segment
    const findNavItem = (items) => {
      for (const item of items) {
        if (item.title.toLowerCase() === segmentLower) return item;

        if (item.to) {
          const toVal = typeof item.to === 'string' ? item.to : (item.to.name || item.to.path || '');
          if (toVal.toLowerCase().includes(segmentLower)) return item;
        }

        if (item.children) {
          const found = findNavItem(item.children);
          if (found) return found;
        }
      }
      return null;
    };

    const navItem = findNavItem(navigationItems);
    const isLast = index === pathSegments.length - 1;

    return {
      name: navItem ? navItem.title : formatSegmentName(segment),
      path: isLast ? null : currentPathBuilder,
    };
  });
});
</script>

<style lang="scss" scoped>
.breadcrumb {
  align-items: center;
  flex-wrap: wrap;
  margin-left: 60px;
  margin-right: auto;
  display: none;

  @include laptop {
    display: flex;
  }

  &-link {
    text-decoration: none;
    transition: color 0.2s ease;

    &:hover {
      text-decoration: underline;
      color: var(--wp-text-accent);
    }
  }

  &-before-link {
    color: var(--wp-text-secondary);
    font-size: 16px;
    line-height: 24px;
  }

  &-current-link {
    color: var(--wp-text-primary);
    font-size: 16px;
    line-height: 24px;
    display: flex;
    align-items: center;
  }

  &-separator {
    color: var(--wp-text-secondary);
    margin: 0 8px;
    font-size: 16px;
  }
}
</style>
